import { StandardEvents } from '@shopify/events';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, unlockScroll } from '@theme/utilities';
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

/** localStorage: set once the visitor subscribes through any customer form. */
const SUBSCRIBED_KEY = 'newsletter-popup:subscribed';
/** localStorage: when the popup was last shown or dismissed, in milliseconds since the epoch. */
const LAST_SHOWN_KEY = 'newsletter-popup:last-shown';
/** sessionStorage: the popup sent the form; read on the page the full-page post returns to. */
const PENDING_KEY = 'newsletter-popup:pending';
/**
 * sessionStorage marker and form id prefix of the back-in-stock block (`back-in-stock.js`), which
 * posts a customer form too. The popup only reads them; that module owns and removes the marker.
 */
const RESTOCK_PENDING_KEY = 'back-in-stock:pending';
const RESTOCK_FORM_ID_PREFIX = 'BackInStockForm-';

const DAY_MS = 24 * 60 * 60 * 1000;
/** A pending submission expires after this, which leaves time for a bot-protection challenge. */
const PENDING_MAX_AGE_MS = 30 * 60 * 1000;
/** Exit intent arms after a moment, so a pointer already leaving the window at load doesn't count. */
const EXIT_INTENT_ARM_DELAY_MS = 3000;
/** Upward scroll speed, in px per ms, that counts as heading back to the browser bar on touch devices. */
const EXIT_SCROLL_SPEED = 1.5;
/** Pause after a blocking dialog or drawer closes, so the popup doesn't open on top of its exit. */
const RETRY_DELAY_MS = 600;
/** Fallback re-check while blocked, for blockers that close without an observable event. */
const RETRY_INTERVAL_MS = 2000;
/** How long the copy confirmation stays visible. */
const STATUS_CLEAR_MS = 5000;
/** Longest an add to cart holds the popup back, in case a dispatcher never settles its promise. */
const CART_ADD_HOLD_MAX_MS = 10000;

const TEXT_FIELD_SELECTOR =
  'input:not([type="button"], [type="checkbox"], [type="color"], [type="radio"], [type="range"], [type="reset"], [type="submit"]), select, textarea';

/**
 * Other bottom-of-screen UI the popup waits for: Shopify's cookie consent banner and preferences
 * panel (injected by `content_for_header`), which a modal would make unreachable, and the "Welcome
 * back" toast (`snippets/welcome-back.liquid`), which normally leaves the page view to the popup.
 */
const CONSENT_ELEMENT_IDS = ['shopify-pc__banner', 'shopify-pc__prefs'];
const WELCOME_TOAST_SELECTOR = '.welcome-back:not([hidden])';

const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

/**
 * Reads a storage value. Storage can be unavailable (privacy settings, sandboxing) and throw.
 *
 * @param {'localStorage' | 'sessionStorage'} area - The storage area.
 * @param {string} key - The storage key.
 * @returns {string | null} The stored value, or null when missing or unavailable.
 */
function readStorage(area, key) {
  try {
    return window[area].getItem(key);
  } catch {
    return null;
  }
}

/**
 * Writes a storage value, ignoring unavailable or full storage.
 *
 * @param {'localStorage' | 'sessionStorage'} area - The storage area.
 * @param {string} key - The storage key.
 * @param {string} value - The value to store.
 */
function writeStorage(area, key, value) {
  try {
    window[area].setItem(key, value);
  } catch {
    // Nothing is remembered; canPersist() keeps the signup popup closed when localStorage fails.
  }
}

/** @type {boolean | undefined} The canPersist() result, which doesn't change during a page view. */
let persistentStorage;

/**
 * Whether localStorage keeps values across page views. Without it the frequency window and the
 * subscribed flag can't be recorded, so the signup popup would reopen on every page.
 *
 * @returns {boolean}
 */
function canPersist() {
  if (persistentStorage !== undefined) return persistentStorage;

  const key = 'newsletter-popup:probe';
  try {
    window.localStorage.setItem(key, '1');
    persistentStorage = window.localStorage.getItem(key) === '1';
    window.localStorage.removeItem(key);
  } catch {
    persistentStorage = false;
  }

  return persistentStorage;
}

/**
 * Removes a storage value, ignoring unavailable storage.
 *
 * @param {'localStorage' | 'sessionStorage'} area - The storage area.
 * @param {string} key - The storage key.
 */
function removeStorage(area, key) {
  try {
    window[area].removeItem(key);
  } catch {
    // Nothing to remove when storage is unavailable.
  }
}

/**
 * Parses the pending-submission marker.
 *
 * @param {string | null} value - The stored JSON.
 * @returns {{ sectionId: string, time: number } | null} The marker, or null when missing or malformed.
 */
function parsePending(value) {
  if (!value) return null;

  try {
    const data = JSON.parse(value);
    if (typeof data?.sectionId === 'string' && typeof data?.time === 'number') return data;
  } catch {
    // A malformed marker is treated as missing.
  }

  return null;
}

/**
 * Whether the customer form result on this page came from a back-in-stock block. The posting
 * form's id becomes the URL hash; without a hash (a bot-protection redirect drops it) the newer of
 * the two sessionStorage markers names the sender.
 *
 * @returns {boolean}
 */
function wasSentByRestockForm() {
  let hashId = window.location.hash.slice(1);
  try {
    hashId = decodeURIComponent(hashId);
  } catch {
    // An undecodable hash is compared as is.
  }

  if (hashId.startsWith(RESTOCK_FORM_ID_PREFIX)) return true;

  // Another form's id in the hash names the form that posted.
  if (hashId && document.getElementById(hashId) instanceof HTMLFormElement) return false;

  let restockTime = NaN;
  try {
    restockTime = JSON.parse(readStorage('sessionStorage', RESTOCK_PENDING_KEY) ?? 'null')?.time;
  } catch {
    // A malformed marker is treated as missing.
  }

  if (typeof restockTime !== 'number' || Number.isNaN(restockTime)) return false;

  const age = Date.now() - restockTime;
  if (age < 0 || age >= PENDING_MAX_AGE_MS) return false;

  const own = parsePending(readStorage('sessionStorage', PENDING_KEY));
  return !own || own.time <= restockTime;
}

/**
 * Whether a back-in-stock block rendered the result of an email that is already subscribed. Shopify
 * reports that post with neither a success nor errors, so this popup's own form state stays empty.
 *
 * @returns {boolean}
 */
function hasRestockAlreadySubscribedResult() {
  return document.querySelector('back-in-stock-component[data-form-state="existing"]') !== null;
}

/**
 * Whether an element is rendered, visible and inside the viewport. A dismissed banner can stay in
 * the DOM hidden or moved off screen, which must not hold the popup back.
 *
 * @param {Element | null} element - The element to check.
 * @returns {boolean}
 */
function isOnScreen(element) {
  if (!element || element.getClientRects().length === 0) return false;
  if (getComputedStyle(element).visibility === 'hidden') return false;

  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
}

/**
 * Ties a timer to an abort signal so aborting the signal also clears the timer.
 *
 * @param {AbortSignal} signal - The signal that cancels the timer.
 * @param {() => void} callback - The timer callback.
 * @param {number} delay - The delay in milliseconds.
 */
function setAbortableTimeout(signal, callback, delay) {
  if (signal.aborted) return;

  const timer = setTimeout(callback, delay);
  signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
}

/**
 * A newsletter signup popup with a discount code.
 *
 * On the storefront it opens after a delay or on exit intent, at most once per frequency window,
 * and waits while another dialog, drawer, popover or add to cart is in progress. It stays closed
 * when localStorage can't remember a dismissal. The customer form posts the whole page, so
 * submitting stores a marker in sessionStorage; the returned page then reopens the popup with the
 * server-rendered success or error state. A successful signup through any customer form, such as
 * the footer signup, also opens the success panel when it shows a discount code, because every
 * subscriber earns the code. A back-in-stock block's signup is the exception: its result belongs
 * to that block, so the popup stays closed (see #handleRestockResult). On the cart page
 * (`data-result-only`) the popup only shows that result. In the theme editor it opens when its
 * section is selected or re-rendered and closes when the section is deselected.
 *
 * @typedef {object} NewsletterPopupRefs
 * @property {HTMLDialogElement} dialog - The modal dialog.
 * @property {HTMLElement} [heading] - The signup panel heading, focused when the popup opens.
 * @property {HTMLElement} [successHeading] - The success panel heading; rendered after a successful post.
 * @property {HTMLInputElement} [emailInput] - The email field.
 * @property {HTMLElement} [error] - The server-rendered error message; rendered after a failed post.
 * @property {HTMLElement} [code] - The discount code.
 * @property {HTMLElement} [status] - The single live region announcing copy results.
 * @property {HTMLImageElement} [image] - The optional popup image.
 *
 * @extends {DialogComponent}
 */
class NewsletterPopupComponent extends DialogComponent {
  /**
   * What the popup shows when it opens: the signup form, or the result of this popup's post.
   * @type {'signup' | 'success' | 'error'}
   */
  #mode = 'signup';

  /** @type {AbortController | undefined} Listeners that live while the element is connected. */
  #controller;

  /** @type {AbortController | undefined} The delay timer and exit-intent listeners. */
  #triggerController;

  /** @type {AbortController | undefined} Listeners that retry opening once nothing blocks it. */
  #waitController;

  /** @type {EventTarget | undefined} The element whose scroll events drive touch exit intent. */
  #scrollTarget;

  /** @type {{ top: number, time: number } | undefined} */
  #lastScroll;

  #maxScrollTop = 0;
  #scrollFrame = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #statusTimer;

  /** @type {Set<object>} One token per add to cart still in flight; see #handleCartLinesUpdate. */
  #pendingCartAdds = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    const { dialog } = this.refs;
    dialog.addEventListener('cancel', this.#handleCancel, { signal });
    dialog.addEventListener('close', this.#handleNativeClose, { signal });

    if (this.#isDesignMode) {
      this.#listenToEditor(signal);
      return;
    }

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });

    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#disarmTriggers();
    this.#stopWaiting();
    this.#pendingCartAdds.clear();
    clearTimeout(this.#statusTimer);
  }

  /**
   * Remembers that this popup sent the form, so the page returned by the full-page post can
   * reopen the popup with the result. Only fires for submissions that pass validation.
   */
  handleSubmit() {
    if (this.#isDesignMode) return;

    writeStorage(
      'sessionStorage',
      PENDING_KEY,
      JSON.stringify({ sectionId: this.dataset.sectionId ?? '', time: Date.now() })
    );
  }

  /**
   * Copies the discount code. When the clipboard is unavailable, selects the code so the visitor
   * can copy it manually. Either result is announced through the status region.
   *
   * @param {MouseEvent} event - The click event; its target is the copy button.
   */
  async copyCode(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const code = button.dataset.code?.trim();
    if (!code) return;

    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch {
      // The Clipboard API is missing (insecure context) or permission was denied.
    }

    if (!copied) this.#selectCode();

    const { status } = this.refs;
    if (!(status instanceof HTMLElement)) return;

    clearTimeout(this.#statusTimer);
    this.#announce(status.dataset[copied ? 'copySuccess' : 'copyError'] ?? '');

    // A confirmation clears after a while; the manual-copy instructions stay.
    if (copied) {
      this.#statusTimer = setTimeout(() => {
        status.textContent = '';
      }, STATUS_CLEAR_MS);
    }
  }

  get #isDesignMode() {
    return this.hasAttribute('data-design-mode');
  }

  /**
   * Decides what the popup does on this page view: show the result of a signup, or arm the delay
   * and exit-intent triggers for an eligible visitor.
   */
  #initialize() {
    // Shopify's bot-protection challenge page renders the theme too; never interrupt it.
    if (window.location.pathname.endsWith('/challenge')) return;

    const formState = this.dataset.formState ?? '';
    // A customer form's success redirect adds this flag; a contact form's doesn't.
    const customerPosted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';

    // A back-in-stock signup shows its result in its own block, never in this popup, and the popup
    // doesn't open over it on this page view.
    if ((formState !== '' || customerPosted || hasRestockAlreadySubscribedResult()) && wasSentByRestockForm()) {
      this.#handleRestockResult(formState === 'success' || (formState === '' && customerPosted));
      return;
    }

    if (formState !== '') {
      const sentHere = this.#wasSentHere();
      removeStorage('sessionStorage', PENDING_KEY);

      // Customer forms share their posted state, so a success can come from this popup or from
      // another customer form on the page, such as the footer signup. Either one subscribes.
      const succeeded = formState === 'success';
      const subscribed = succeeded && (sentHere || customerPosted);
      if (subscribed) writeStorage('localStorage', SUBSCRIBED_KEY, '1');

      // Every subscriber earns the code, so another form's signup also opens the success panel
      // when it shows one. The `code` ref renders only when a discount code is set.
      if (sentHere || (subscribed && this.refs.code instanceof HTMLElement)) {
        this.#mode = succeeded ? 'success' : 'error';
        this.#clearPostedUrl();
        this.#requestOpen();
        return;
      }

      // The success panel replaced the signup form on this page, so there is nothing to offer.
      if (succeeded) return;

      // An error from another form renders here too.
      this.#discardOtherFormError();
    } else if (customerPosted) {
      // A signup succeeded but no form state was rendered; record it without showing a result.
      removeStorage('sessionStorage', PENDING_KEY);
      writeStorage('localStorage', SUBSCRIBED_KEY, '1');
      return;
    }

    // The cart page renders the popup only to show a signup's result, never to offer one.
    if (this.hasAttribute('data-result-only')) return;

    if (!this.#isEligible()) return;

    this.#armTriggers();
  }

  /**
   * Handles a page that shows a back-in-stock signup's result: the popup stays closed on this page
   * view and doesn't touch the URL, which the block cleans up. A successful signup joins the email
   * list, so the offer then waits a full frequency window instead of interrupting right away, but
   * it isn't hidden for good: the visitor can still claim the welcome code later. Only a signup
   * through this popup or another newsletter form marks the visitor as subscribed.
   *
   * @param {boolean} succeeded - Whether the signup succeeded.
   */
  #handleRestockResult(succeeded) {
    // An older marker from this popup can't belong to the result on this page.
    removeStorage('sessionStorage', PENDING_KEY);
    this.#discardOtherFormError();

    if (succeeded) writeStorage('localStorage', LAST_SHOWN_KEY, String(Date.now()));
  }

  /**
   * Whether this popup sent the form whose result the page shows. The form id becomes the URL
   * hash after the post; the sessionStorage marker covers redirects that drop it.
   *
   * @returns {boolean}
   */
  #wasSentHere() {
    const { formId, sectionId } = this.dataset;
    if (formId && window.location.hash === `#${formId}`) return true;

    const pending = parsePending(readStorage('sessionStorage', PENDING_KEY));
    if (!pending || pending.sectionId !== sectionId) return false;

    const age = Date.now() - pending.time;
    return age >= 0 && age < PENDING_MAX_AGE_MS;
  }

  /**
   * Removes this popup's form id hash and the `customer_posted` flag, which Shopify reads to
   * render the success state, so reloading the page doesn't reopen the result.
   */
  #clearPostedUrl() {
    const { formId } = this.dataset;

    try {
      const url = new URL(window.location.href);
      const hasFormHash = Boolean(formId) && url.hash === `#${formId}`;
      const hasPostedFlag = url.searchParams.has('customer_posted');
      if (!hasFormHash && !hasPostedFlag) return;

      if (hasFormHash) url.hash = '';
      if (hasPostedFlag) url.searchParams.delete('customer_posted');
      history.replaceState(history.state, '', url);
    } catch {
      // Leaving the URL in place only means a reload shows the result again.
    }
  }

  /**
   * Hides an error that another customer form's post rendered inside this popup.
   */
  #discardOtherFormError() {
    const { error, emailInput } = this.refs;
    if (!(error instanceof HTMLElement)) return;

    error.hidden = true;

    if (!(emailInput instanceof HTMLInputElement)) return;

    emailInput.removeAttribute('aria-invalid');
    const describedBy = (emailInput.getAttribute('aria-describedby') ?? '')
      .split(' ')
      .filter((id) => id && id !== error.id)
      .join(' ');

    if (describedBy) {
      emailInput.setAttribute('aria-describedby', describedBy);
    } else {
      emailInput.removeAttribute('aria-describedby');
    }
  }

  /**
   * Whether the signup popup may open for this visitor.
   *
   * @returns {boolean}
   */
  #isEligible() {
    if (this.hasAttribute('data-suppressed')) return false;
    // Dismissals and signups couldn't be remembered, so the popup would reopen on every page.
    if (!canPersist()) return false;
    if (readStorage('localStorage', SUBSCRIBED_KEY)) return false;

    const lastShown = Number(readStorage('localStorage', LAST_SHOWN_KEY));
    if (!lastShown) return true;

    const elapsed = Date.now() - lastShown;
    const frequency = Math.max(1, Number(this.dataset.frequencyDays) || 1) * DAY_MS;

    // A timestamp from the future (changed clock) does not block the popup.
    return elapsed < 0 || elapsed >= frequency;
  }

  /**
   * Starts the delay timer and, when enabled, exit-intent detection.
   */
  #armTriggers() {
    this.#disarmTriggers();
    this.#triggerController = new AbortController();
    const { signal } = this.#triggerController;

    this.#preloadImage();

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    const startTimer = () => setAbortableTimeout(signal, () => this.#requestOpen(), delay);

    // With no delay, wait for the page to finish loading so the popup doesn't compete with it.
    if (delay === 0 && document.readyState !== 'complete') {
      window.addEventListener('load', startTimer, { once: true, signal });
    } else {
      startTimer();
    }

    if (!this.hasAttribute('data-exit-intent')) return;

    if (finePointerQuery.matches) {
      setAbortableTimeout(
        signal,
        () => document.addEventListener('mouseout', this.#handleMouseOut, { signal }),
        EXIT_INTENT_ARM_DELAY_MS
      );
      return;
    }

    // The scroll container changes at the desktop breakpoint (see @theme/scroll-container).
    this.#bindScrollListener(signal);
    scrollContainerMediaQuery.addEventListener('change', () => this.#bindScrollListener(signal), { signal });
    signal.addEventListener('abort', () => cancelAnimationFrame(this.#scrollFrame), { once: true });
  }

  #disarmTriggers() {
    this.#triggerController?.abort();
    this.#triggerController = undefined;
    this.#scrollTarget = undefined;
  }

  /**
   * Listens to scroll events on the current scroll container.
   *
   * @param {AbortSignal} signal - Removes the listener when the triggers are disarmed.
   */
  #bindScrollListener(signal) {
    if (signal.aborted) return;

    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true, signal });

    this.#lastScroll = undefined;
    this.#maxScrollTop = getScrollTop();
  }

  /**
   * Desktop exit intent: the pointer leaves the window through its top edge.
   *
   * @param {MouseEvent} event - The mouseout event.
   */
  #handleMouseOut = (event) => {
    if (event.relatedTarget || event.clientY > 0) return;

    this.#requestOpen();
  };

  /**
   * Touch exit intent: a fast scroll back toward the top after reading further down the page,
   * which usually means the visitor is reaching for the browser bar.
   */
  #handleScroll = () => {
    if (this.#scrollFrame) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;

      const top = getScrollTop();
      const time = performance.now();
      const previous = this.#lastScroll;

      this.#lastScroll = { top, time };
      this.#maxScrollTop = Math.max(this.#maxScrollTop, top);

      if (!previous) return;

      const elapsed = time - previous.time;
      if (elapsed <= 0 || elapsed > 250) return;

      const viewportHeight = window.innerHeight;
      const upwardSpeed = (previous.top - top) / elapsed;

      if (this.#maxScrollTop > viewportHeight * 1.5 && top < viewportHeight && upwardSpeed > EXIT_SCROLL_SPEED) {
        this.#requestOpen();
      }
    });
  };

  /**
   * Starts loading the image before the popup opens, unless the image is hidden on this viewport.
   */
  #preloadImage() {
    const { image } = this.refs;
    if (!(image instanceof HTMLImageElement)) return;
    if (!mediaQueryLarge.matches && !this.hasAttribute('data-image-on-mobile')) return;

    image.loading = 'eager';
  }

  /**
   * Opens the popup when nothing else needs the visitor's attention; otherwise waits.
   */
  #requestOpen() {
    if (!this.isConnected || this.refs.dialog.open) return;

    if (this.#mode === 'signup') {
      if (!this.#isEligible()) {
        this.#disarmTriggers();
        return;
      }

      if (!this.hasAttribute('data-show-on-mobile') && !mediaQueryLarge.matches) return;
    }

    if (this.#isBlocked()) {
      this.#waitUntilUnblocked();
      return;
    }

    this.#stopWaiting();
    this.#disarmTriggers();
    this.#setInitialFocus();
    this.showDialog();
  }

  /**
   * Whether opening now would interrupt the visitor: the tab is hidden, an add to cart is in
   * flight, another dialog, drawer, popover or scroll-locking menu is open, the cookie consent
   * banner or the "Welcome back" toast shows, or they are typing in a field.
   *
   * @returns {boolean}
   */
  #isBlocked() {
    if (document.hidden) return true;
    if (this.#pendingCartAdds.size > 0) return true;
    if (document.documentElement.hasAttribute('scroll-lock')) return true;
    if (document.querySelector('theme-drawer[open], shopify-chat[open]')) return true;

    for (const dialog of document.querySelectorAll('dialog[open]')) {
      if (dialog !== this.refs.dialog) return true;
    }

    try {
      if (document.querySelector(':popover-open')) return true;
    } catch {
      // Browsers without the Popover API have no open popovers.
    }

    // The visitor must be able to answer the cookie banner first, and a showing toast finishes first.
    for (const id of CONSENT_ELEMENT_IDS) {
      if (isOnScreen(document.getElementById(id))) return true;
    }
    if (document.querySelector(WELCOME_TOAST_SELECTOR)) return true;

    const { activeElement } = document;
    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      // A signup result opens as the page loads. Another customer form can render the shared
      // posted error with `autofocus`, which focuses its field without the visitor typing there.
      const autofocused = this.#mode !== 'signup' && activeElement.hasAttribute('autofocus');
      const isTextField = activeElement.isContentEditable || activeElement.matches(TEXT_FIELD_SELECTOR);
      if (isTextField && !autofocused) return true;
    }

    return false;
  }

  /**
   * Holds the popup back while an add to cart is in flight. With an empty cart the drawer opens
   * only after the request settles, so #isBlocked() can't see it yet. Only adds auto-open an
   * overlay; quantity changes and removals don't.
   *
   * @param {Event} event - The cart lines update event.
   */
  #handleCartLinesUpdate = (event) => {
    const { action, promise } = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
    const signal = this.#controller?.signal;
    if (action !== 'add' || typeof promise?.then !== 'function' || !signal) return;

    const token = {};
    const release = () => {
      this.#pendingCartAdds.delete(token);
    };

    this.#pendingCartAdds.add(token);
    Promise.resolve(promise).then(release, release);
    setAbortableTimeout(signal, release, CART_ADD_HOLD_MAX_MS);
  };

  /**
   * Retries opening after blockers close. Dialog `close` and `toggle` don't bubble, so they are
   * caught in the capture phase.
   */
  #waitUntilUnblocked() {
    if (this.#waitController) return;

    this.#waitController = new AbortController();
    const { signal } = this.#waitController;

    let retryScheduled = false;
    const retry = () => {
      if (retryScheduled) return;
      retryScheduled = true;

      setAbortableTimeout(
        signal,
        () => {
          this.#stopWaiting();
          this.#requestOpen();
        },
        RETRY_DELAY_MS
      );
    };

    document.addEventListener('close', retry, { capture: true, signal });
    document.addEventListener('toggle', retry, { capture: true, signal });
    document.addEventListener('focusout', retry, { signal });
    document.addEventListener('visibilitychange', retry, { signal });
    document.addEventListener('theme-drawer:close', retry, { signal });

    const interval = setInterval(() => {
      if (!this.#isBlocked()) retry();
    }, RETRY_INTERVAL_MS);
    signal.addEventListener('abort', () => clearInterval(interval), { once: true });
  }

  #stopWaiting() {
    this.#waitController?.abort();
    this.#waitController = undefined;
  }

  /**
   * The element that receives focus when the popup opens: the email field after a failed post,
   * otherwise the visible heading. Focusing the heading keeps touch keyboards closed.
   *
   * @returns {HTMLElement | null}
   */
  #getInitialFocusTarget() {
    const { emailInput, heading, successHeading } = this.refs;

    if (this.#mode === 'error' && emailInput instanceof HTMLInputElement) return emailInput;

    for (const candidate of [successHeading, heading]) {
      if (!(candidate instanceof HTMLElement) || candidate.closest('[hidden]')) continue;

      // A focused .visually-hidden element becomes visible, so a hidden fallback heading is skipped.
      if (!candidate.classList.contains('visually-hidden')) return candidate;
    }

    return this.refs.dialog;
  }

  /**
   * Marks the initial focus target with `autofocus`, which `showModal()` honors, so focus never
   * lands on the email field first and opens a touch keyboard.
   */
  #setInitialFocus() {
    const target = this.#getInitialFocusTarget();

    for (const element of this.refs.dialog.querySelectorAll('[autofocus]')) {
      if (element !== target) element.removeAttribute('autofocus');
    }

    target?.setAttribute('autofocus', '');
  }

  #handleDialogOpen = () => {
    if (!this.#isDesignMode && this.#mode === 'signup') {
      writeStorage('localStorage', LAST_SHOWN_KEY, String(Date.now()));
    }

    const target = this.#getInitialFocusTarget();
    if (target && document.activeElement !== target) target.focus();
  };

  /**
   * Routes a native cancel (Android back gesture, close watchers) through the animated close.
   *
   * @param {Event} event - The dialog's cancel event.
   */
  #handleCancel = (event) => {
    if (!event.cancelable) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * A native close that bypasses closeDialog() must still release the scroll lock taken by
   * showDialog() and count as a dismissal. Repeating this after closeDialog() is harmless.
   */
  #handleNativeClose = () => {
    const { dialog } = this.refs;

    unlockScroll(dialog);
    dialog.classList.remove('dialog-closing');
    this.#handleDialogClose();
  };

  #handleDialogClose = () => {
    if (!this.#isDesignMode) {
      // Closing counts as a dismissal; the frequency window starts now.
      writeStorage('localStorage', LAST_SHOWN_KEY, String(Date.now()));
      this.#mode = 'signup';
    }

    clearTimeout(this.#statusTimer);

    const { status } = this.refs;
    if (status instanceof HTMLElement) status.textContent = '';
  };

  /**
   * Announces a message through the status region. Clearing it first lets a repeated message be
   * announced again.
   *
   * @param {string} message - The message to announce.
   */
  #announce(message) {
    const { status } = this.refs;
    if (!(status instanceof HTMLElement)) return;

    status.textContent = '';
    if (!message) return;

    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  /**
   * Selects the discount code text so it can be copied manually.
   */
  #selectCode() {
    const { code } = this.refs;
    const selection = window.getSelection();
    if (!(code instanceof HTMLElement) || !selection) return;

    const range = document.createRange();
    range.selectNodeContents(code);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /**
   * Theme editor: open while the section is selected or re-rendered, close when it's deselected.
   *
   * @param {AbortSignal} signal - Removes the listeners on disconnect.
   */
  #listenToEditor(signal) {
    /**
     * @param {Event} event - A theme editor section event.
     * @returns {boolean}
     */
    const isThisSection = (event) => {
      // The editor dispatches section events on the section's wrapper element.
      if (event.target instanceof Element && event.target === this.closest('.shopify-section')) return true;

      const detail = event instanceof CustomEvent ? event.detail : undefined;
      return Boolean(detail?.sectionId) && detail.sectionId === this.dataset.sectionId;
    };

    const open = (/** @type {Event} */ event) => {
      if (!isThisSection(event)) return;

      this.#setInitialFocus();
      this.showDialog();
    };

    document.addEventListener('shopify:section:select', open, { signal });
    document.addEventListener('shopify:section:load', open, { signal });
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (isThisSection(event)) this.closeDialog();
      },
      { signal }
    );
  }
}

if (!customElements.get('newsletter-popup-component')) {
  customElements.define('newsletter-popup-component', NewsletterPopupComponent);
}
